import { TabView, TabPanel } from "primereact/tabview";
import { GetUserCoursesApiResponseDto, GetUserModulesApiResponseDto, MessageDto, UserDto } from "../../general/dtos";
import { UserDetails } from "./user-details";
import { UserModuleAssignments } from "./user-module-assignments";
import { Api } from "../../general/api";
import { useContext, useEffect, useState } from "react";
import { GeneralErrorDialog } from "../dialogs/error-dialog";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { AxiosError } from "axios";
import { UserManagementDataContext, UserManagementDataContextValue } from "../../providers/user-management-provider";
import UserLearningPath from "./user-leaarning-path";
import { UserCourseAssignments } from "./user-course-assignments";
import { useApi } from "../../providers/api-provider";
import { UserEditor } from "./user-edit";
import { UserQuizResults } from "./user-quiz-results";
import { useBookmarkTab } from '../../general/bookmark-selection';

interface UserManagementPageProps {
    selectedUsers: UserDto[];
}

export default function UserManagementPage({ selectedUsers }: UserManagementPageProps) {
  const [activeTab, setActiveTab] = useBookmarkTab(['activities', 'courses', 'modules', 'learning-path', 'quiz-results']);
  const ctx: UserManagementDataContextValue | null = useContext<UserManagementDataContextValue | null>(UserManagementDataContext);
  const { allModulesError, userModulesError, userError, userModules, userCourses, setUserModulesForUser, setUserCoursesForUser, mode } = ctx!;

  const api: Api = useApi();
  const [error, setError] = useState<string>('');
  const [errorTitle, setErrorTitle] = useState<string>('');
  const [errorDetails, setErrorDetails] = useState<string>('');

  useEffect(() => {
    if (selectedUsers.length === 1) {
        const sub = selectedUsers[0].sub;
        if (!userModules[sub]) ctx?.loadUserModules(sub);
        if (!userCourses[sub]) ctx?.loadUserCourses(sub);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUsers]);

  useEffect(() => {
    if (allModulesError) {
        setError(allModulesError);
        setErrorTitle('Error loading module list');
        setErrorDetails('There was an unhandled error while trying to load the module list. Please try again later or contact support :)');
    }
    if (userModulesError) {
        setError(userModulesError);
        setErrorTitle('Error loading user assignments');
        setErrorDetails('There was an unhandled error while trying to load user assignments. Please try again later or contact support :)');
    }
    if (userError) {
        setError(userError);
        setErrorTitle('Error loading or saving the user information');
        setErrorDetails('There was an unhandled error while trying to loador save the user data. Please try again later or contact support :)');
    }
  }, [allModulesError, userModulesError, userError]);
  
  const saveUserModules = async (sub: string, assigned: string[]): Promise<GetUserModulesApiResponseDto | MessageDto | null> => {
    try {
        const json: GetUserModulesApiResponseDto | MessageDto = await api.saveUserModules(sub, assigned);
        if ('data' in json) {
            // keep the provider up to date
            setUserModulesForUser(sub, (json as GetUserModulesApiResponseDto).data);
        }
        return json;
    } catch (error: AxiosError | any) {
        const message: string = error.response?.data?.message ?? 'Save error';
        setError(message);
        setErrorTitle('Error saving user module assignments');
        setErrorDetails('There was an unhandled error while trying to save user assignments. Please try again later or contact support :)');
    }
    return null;
  }

  const saveUserCourses = async (sub: string, assigned: string[]): Promise<GetUserCoursesApiResponseDto | MessageDto | null> => {
    try {
        const json: GetUserCoursesApiResponseDto | MessageDto = await api.saveUserCourses(sub, assigned);
        if ('data' in json) {
            // keep the provider up to date
            setUserCoursesForUser(sub, (json as GetUserCoursesApiResponseDto).data);
        }
        return json;
    } catch (error: AxiosError | any) {
        const message: string = error.response?.data?.message ?? 'Save error';
        setError(message);
        setErrorTitle('Error saving user course assignments');
        setErrorDetails('There was an unhandled error while trying to save user assignments. Please try again later or contact support :)');
    }
    return null;
  }

  return (<>

      {mode === "edit" && <>
          <UserEditor />
      </>}

      {mode === "view" && <><TabView activeIndex={activeTab} onTabChange={event => setActiveTab(event.index)} className="flex flex-column h-full">
        <TabPanel header="User activities">
        <div className="h-full" style={{ height: "100%"/*, backgroundColor: 'green'*/ }}>
            <UserDetails
                selectedUsers={selectedUsers}
            />
        </div>
        </TabPanel>

        <TabPanel header="Courses">
        <div className="flex flex-column h-full" style={{ minHeight: 0/*, backgroundColor: 'blue'*/ }}>
            <UserCourseAssignments
                selectedUsers={selectedUsers}
                saveUserCourses={saveUserCourses}
             />
        </div>
        </TabPanel>

        <TabPanel header="Modules">
        <div className="flex flex-column h-full" style={{ minHeight: 0/*, backgroundColor: 'blue'*/ }}>
            <UserModuleAssignments
                selectedUsers={selectedUsers}
                saveUserModules={saveUserModules}
             />
        </div>
        </TabPanel>

        <TabPanel header="Learning path">
        <div className="flex flex-column h-full" style={{ minHeight: 0/*, backgroundColor: 'blue'*/ }}>
            <UserLearningPath userKey={selectedUsers.length === 1 ? selectedUsers[0].sub : undefined} />
        </div>
        </TabPanel>
        <TabPanel header="Quiz results">
            <UserQuizResults selectedUsers={selectedUsers} />
        </TabPanel>
    </TabView>
    </>}

    <GeneralErrorDialog
        visible={error.length > 0}
        title={errorTitle}
        message={error}
        details={errorDetails}
        onClose={() => setError('')}
    />
  </>);
}
